import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useAIConversationChatMessageQuery } from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { AIMarkdownResponse } from '../../../../web/components-v2/ai/AiMarkdownResponse';
import {
	PromptInput,
	PromptInputBody,
	PromptInputTextarea,
	PromptInputFooter,
	PromptInputTools,
	PromptInputSubmit,
	PromptInputSpeechButton,
} from '../../../../web/components-v2/ai/PromptInput';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { SimpleTooltip } from '../../../../web/components/Tooltip';
import { SparklesIcon } from '../../../../web/components/svgs/icons/SparklesIcon';
import { BackArrowIcon } from '../../../../web/components/svgs/icons/BackArrowIcon';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useTysonCoaching } from '../../contexts/TysonCoachingContext';
import { useWebsocketConnectionStatus } from '../../hooks/useWebsocketConnectionStatus';
import { parseCoachingMessage } from '../../utils/parseCoachingMessage';
import { styleSheet } from './styles';

export const TysonCoachingPanel: React.FC = () => {
	const {
		coachingMessages,
		isCreatingConversation,
		isAIResponding,
		isActive,
		viewingHistoricalConversationId,
		clearHistoricalConversation,
		requestSuggestion,
		sendDirectQuestion,
		callEndedPendingSummary,
		isCallConnected,
		requestPostCallSummary,
	} = useTysonCoaching();

	const userSession = useUserSession();
	const autoSuggestionsEnabled = userSession.user?.userPreferences?.coffeePreferences?.autoSuggestionsEnabled ?? false;

	const wsStatus = useWebsocketConnectionStatus();
	const isWsConnected = wsStatus === Api.RemoteEventsConnectionStatus.Connected;

	// State for text input
	const [inputValue, setInputValue] = React.useState('');
	const [isHistoryExpanded, setIsHistoryExpanded] = React.useState(false);
	const [isGamePlanCollapsed, setIsGamePlanCollapsed] = React.useState(false);
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);

	// Handle direct question submission
	const handleDirectQuestion = React.useCallback(
		(message: { text: string; files: unknown[] }, e: React.FormEvent) => {
			e.preventDefault();
			if (!message.text.trim() || isAIResponding) return;

			sendDirectQuestion(message.text);
			setInputValue('');
		},
		[isAIResponding, sendDirectQuestion]
	);

	// Fetch historical conversation messages if viewing one
	const { data: historicalMessages, isLoading: isLoadingHistorical } = useAIConversationChatMessageQuery({
		id: viewingHistoricalConversationId,
		enabled: !!viewingHistoricalConversationId,
	});

	// Filter to only show assistant messages from historical conversation
	const historicalAssistantMessages = React.useMemo(() => {
		if (!historicalMessages) return [];
		return historicalMessages.filter(msg => msg.role === Api.AIChatRole.Assistant);
	}, [historicalMessages]);

	// The first assistant message is always the Game Plan
	const gamePlanMessage = React.useMemo(() => {
		for (const msg of coachingMessages) {
			if (msg.role === 'assistant') return msg;
		}
		return null;
	}, [coachingMessages]);

	// Split messages: latest assistant message → hero card; rest → history
	const latestAssistantMessage = React.useMemo(() => {
		for (let i = coachingMessages.length - 1; i >= 0; i--) {
			if (coachingMessages[i].role === 'assistant' && coachingMessages[i] !== gamePlanMessage) {
				return coachingMessages[i];
			}
		}
		return null;
	}, [coachingMessages, gamePlanMessage]);

	const historyMessages = React.useMemo(() => {
		if (!latestAssistantMessage) return coachingMessages;
		const latestIdx = coachingMessages.lastIndexOf(latestAssistantMessage);
		return coachingMessages.slice(0, latestIdx);
	}, [coachingMessages, latestAssistantMessage]);

	const handleGetSummary = React.useCallback(() => {
		setIsGamePlanCollapsed(true);
		requestPostCallSummary();
	}, [requestPostCallSummary]);

	// The summary now fires on its own when the call ends, so collapse the sticky
	// Game Plan on disconnect to leave room for it — the rep can reopen it.
	const wasCallConnectedRef = React.useRef(isCallConnected);
	React.useEffect(() => {
		if (wasCallConnectedRef.current && !isCallConnected) {
			setIsGamePlanCollapsed(true);
		}
		wasCallConnectedRef.current = isCallConnected;
	}, [isCallConnected]);

	if (isCreatingConversation) {
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.header)}>
					<h3 className={css(styleSheet.title)}>
						<SparklesIcon />
						AI Sales Coach
					</h3>
					<p className={css(styleSheet.subtitle)}>Real-time coaching during your call</p>
				</div>
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='small' />
					<span>Preparing your AI coach...</span>
				</div>
			</div>
		);
	}

	// Viewing a historical conversation
	if (viewingHistoricalConversationId) {
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.header)}>
					<div className={css(styleSheet.headerWithBack)}>
						<button
							type='button'
							className={css(styleSheet.backButton)}
							onClick={clearHistoricalConversation}
							aria-label='Back to current session'
						>
							<BackArrowIcon />
						</button>
						<div>
							<h3 className={css(styleSheet.title)}>
								<SparklesIcon />
								Previous Session
							</h3>
							<p className={css(styleSheet.subtitle)}>
								{isActive ? 'Viewing previous coaching session' : 'Historical coaching session'}
							</p>
						</div>
					</div>
				</div>

				<div className={css(styleSheet.messagesContainer)}>
					{isLoadingHistorical ? (
						<div className={css(styleSheet.loadingContainer)}>
							<LoadingSpinner type='small' />
							<span>Loading conversation...</span>
						</div>
					) : historicalAssistantMessages.length === 0 ? (
						<div className={css(styleSheet.emptyState)}>
							<p>No coaching messages in this session.</p>
						</div>
					) : (
						[...historicalAssistantMessages].reverse().map((message, index) => (
							<div key={message.id || index} className={css(styleSheet.messageCard)}>
								<div className={css(styleSheet.messageHeader)}>
									<span className={css(styleSheet.messageLabel)}>AI Coach</span>
								</div>
								<div className={css(styleSheet.messageContent)}>
									<AIMarkdownResponse>{message.text}</AIMarkdownResponse>
								</div>
							</div>
						))
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<h3 className={css(styleSheet.title)}>
					<SparklesIcon />
					AI Sales Coach
					{isActive && (
						<SimpleTooltip
							title={
								isWsConnected
									? 'Connected — this window is receiving Twilio webhooks'
									: 'Not connected — this window is not receiving Twilio webhooks'
							}
						>
							<span
								className={css(
									styleSheet.connectionBulb,
									isWsConnected ? styleSheet.connectionBulbConnected : styleSheet.connectionBulbDisconnected
								)}
								aria-label={isWsConnected ? 'Twilio connected' : 'Twilio disconnected'}
							/>
						</SimpleTooltip>
					)}
					{isAIResponding && (
						<span className={css(styleSheet.streamingIndicator)}>
							<LoadingSpinner type='small' />
							Analyzing...
						</span>
					)}
				</h3>
				<p className={css(styleSheet.subtitle)}>Real-time coaching during your call</p>
			</div>

			<div
				className={css(
					styleSheet.messagesContainer,
					gamePlanMessage && !isGamePlanCollapsed && styleSheet.messagesContainerHalf
				)}
			>
				{/* Empty state — no messages yet */}
				{coachingMessages.length === 0 && (
					<div className={css(styleSheet.emptyState)}>
						<div className={css(styleSheet.emptyStateIcon)}>💬</div>
						<p>Your AI coach will provide suggestions when you request them.</p>
					</div>
				)}

				{/* Latest coaching hero card */}
				{latestAssistantMessage &&
					(() => {
						const parsed = parseCoachingMessage(latestAssistantMessage.text);
						return (
							<div className={css(styleSheet.latestCoachingCard)}>
								<div className={css(styleSheet.latestCoachingHeader)}>🎯 Latest Coaching</div>
								{parsed ? (
									<>
										<div className={css(styleSheet.sayThisBubble)}>
											<div className={css(styleSheet.sayThisLabel)}>💬 Say this:</div>
											<p className={css(styleSheet.sayThisText)}>{parsed.suggestion}</p>
										</div>
										{parsed.reasoning.length > 0 && (
											<div className={css(styleSheet.reasoningRow)}>
												<span className={css(styleSheet.reasoningIcon)}>ℹ️</span>
												<p className={css(styleSheet.reasoningText)}>{parsed.reasoning}</p>
											</div>
										)}
									</>
								) : (
									<div className={css(styleSheet.messageContent)}>
										<AIMarkdownResponse>{latestAssistantMessage.text}</AIMarkdownResponse>
									</div>
								)}
							</div>
						);
					})()}

				{/* History toggle */}
				{historyMessages.length > 0 && (
					<button
						type='button'
						className={css(styleSheet.historyToggleButton)}
						onClick={() => setIsHistoryExpanded(v => !v)}
					>
						{isHistoryExpanded ? '↑ Hide history' : 'Full conversation history ↓'}
					</button>
				)}

				{/* History list */}
				{isHistoryExpanded &&
					[...historyMessages].reverse().map((message, index) => {
						if (message.messageType === 'system') {
							return (
								<div key={message.id || index} className={css(styleSheet.systemMessage)}>
									<span className={css(styleSheet.systemMessageText)}>{message.text}</span>
								</div>
							);
						}
						return (
							<div
								key={message.id || index}
								className={css(message.role === 'user' ? styleSheet.userMessage : styleSheet.assistantMessage)}
							>
								<div className={css(styleSheet.messageHeader)}>
									<span
										className={css(
											message.role === 'user' ? styleSheet.userMessageLabel : styleSheet.assistantMessageLabel
										)}
									>
										{message.role === 'user'
											? message.messageType === 'transcript'
												? 'Transcript Sent'
												: 'Your Question'
											: 'AI Coach'}
									</span>
									<span className={css(styleSheet.messageTimestamp)}>{message.timestamp.toLocaleTimeString()}</span>
								</div>
								<div className={css(styleSheet.messageContent)}>
									<AIMarkdownResponse>{message.text}</AIMarkdownResponse>
								</div>
							</div>
						);
					})}
			</div>

			{/* Sticky Game Plan card */}
			{gamePlanMessage && (
				<div className={css(styleSheet.gamePlanCard, !isGamePlanCollapsed && styleSheet.gamePlanCardExpanded)}>
					<button
						type='button'
						className={css(styleSheet.gamePlanToggle)}
						onClick={() => setIsGamePlanCollapsed(v => !v)}
					>
						<span className={css(styleSheet.gamePlanHeader)}>Game Plan</span>
						<span>{isGamePlanCollapsed ? '▼' : '▲'}</span>
					</button>
					{!isGamePlanCollapsed && (
						<div className={css(styleSheet.messageContent)}>
							<AIMarkdownResponse>{gamePlanMessage.text}</AIMarkdownResponse>
						</div>
					)}
				</div>
			)}

			{/* Get Suggestion button - shown during active call when auto-suggestions are off */}
			{isActive && isCallConnected && !autoSuggestionsEnabled && (
				<div className={css(styleSheet.buttonContainer)}>
					<button
						type='button'
						className={css(styleSheet.getSuggestionButton)}
						onClick={requestSuggestion}
						disabled={isAIResponding}
					>
						{isAIResponding ? 'Analyzing...' : 'Get Suggestion'}
					</button>
				</div>
			)}

			{/* Get Summary button - shown after call ends */}
			{isActive && callEndedPendingSummary && (
				<div className={css(styleSheet.buttonContainer)}>
					<button
						type='button'
						className={css(styleSheet.getSuggestionButton)}
						onClick={handleGetSummary}
						disabled={isAIResponding}
					>
						{isAIResponding ? 'Generating Summary...' : 'Get Summary'}
					</button>
				</div>
			)}

			{/* Text input for direct questions */}
			{isActive && (
				<PromptInput onSubmit={handleDirectQuestion}>
					<PromptInputBody>
						<PromptInputTextarea
							ref={textareaRef}
							value={inputValue}
							onChange={e => setInputValue(e.target.value)}
							placeholder='What would you like to know?'
							disabled={isAIResponding}
						/>
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} />
						</PromptInputTools>
						<PromptInputSubmit status={isAIResponding ? 'loading' : 'idle'} />
					</PromptInputFooter>
				</PromptInput>
			)}
		</div>
	);
};
